// src/modules/competitions/components/JudgeCategorySelection.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Card from '../../../shared/components/ui/Card';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import { judgeService } from '../services/judgeService';
import { CategoryJudgeStatus } from '../types/judge';

const JudgeCategorySelection: React.FC = () => {
    const navigate = useNavigate();
    const { id: eventId } = useParams<{ id: string }>();
    const location = useLocation();

    const [eventTitle, setEventTitle] = useState('');
    const [categories, setCategories] = useState<CategoryJudgeStatus[]>([]);
    const [selectedLevels, setSelectedLevels] = useState<Record<number, string[]>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [applyingAll, setApplyingAll] = useState(false);
    const [applyingCategory, setApplyingCategory] = useState<number | null>(null);

    // ✅ Obtener termsId de la navegación o cargar desde el backend
    const [termsId, setTermsId] = useState<number | null>(
        location.state?.termsId || null
    );

    useEffect(() => {
        if (eventId) {
            loadEventStatus();

            // Si no tenemos termsId, cargarlo
            if (!termsId) {
                loadTermsId();
            }
        }
    }, [eventId]);

    const loadTermsId = async () => {
        try {
            const response = await judgeService.getTerms(Number(eventId));
            if (response.success && response.data) {
                setTermsId(response.data.id);
            }
        } catch (err) {
            console.error('Error al cargar terms_id:', err);
            setError('Error al cargar términos y condiciones');
        }
    };

    const loadEventStatus = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await judgeService.getStatusByEvent(Number(eventId));

            if (response.success && response.data) {
                setEventTitle(response.data.event_title);
                setCategories(response.data.categories);
            } else {
                setError(response.message || 'Error al cargar categorías');
            }
        } catch (err) {
            setError('Error al cargar información');
        } finally {
            setIsLoading(false);
        }
    };

    const getCategoryImageUrl = (categoryId: number): string => {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://www.ibloom.cl/api';
        return `${API_BASE}/v1/competitions/category-image?category_id=${categoryId}`;
    };

    const handleToggleLevel = (categoryId: number, level: string) => {
        setSelectedLevels(prev => {
            const current = prev[categoryId] || [];
            const isSelected = current.includes(level);

            return {
                ...prev,
                [categoryId]: isSelected
                    ? current.filter(l => l !== level)
                    : [...current, level]
            };
        });
    };

    const handleSelectAllLevels = (categoryId: number) => {
        const category = categories.find(c => c.category_id === categoryId);
        if (!category) return;

        setSelectedLevels(prev => ({
            ...prev,
            [categoryId]: category.available_levels
        }));
    };

    const handleApplyToCategory = async (categoryId: number) => {
        try {
            setApplyingCategory(categoryId);
            setError(null);
            setSuccess(null);

            const selected = selectedLevels[categoryId] || [];

            if (selected.length === 0) {
                setError('Selecciona al menos un nivel');
                return;
            }

            if (!termsId) {
                setError('Error: No se pudo obtener el ID de términos');
                return;
            }

            // ✅ PASO 1: Postular a la categoría
            const applyResponse = await judgeService.apply({
                category_id: categoryId,
                terms_id: termsId
            });

            if (!applyResponse.success) {
                setError(applyResponse.message || 'Error al postular');
                return;
            }

            // ✅ VALIDACIÓN CRÍTICA: Verificar que tenemos judge_id
            const judgeId = applyResponse.data?.judge_id;

            if (!judgeId) {
                setError('Error: No se recibió el ID del juez. Intenta recargar la página.');
                return;
            }

            // ✅ PASO 2: Postular a niveles
            const levelsResponse = await judgeService.applyToLevels({
                judge_id: judgeId,
                levels: selected
            });

            if (!levelsResponse.success) {
                setError(levelsResponse.message || 'Error al postular a niveles');
                return;
            }

            // ✅ PASO 3: Mostrar mensaje de éxito
            setSuccess(`✅ Postulación enviada a ${selected.length} nivel(es). Espera aprobación del administrador.`);
            setError(null);

            // ✅ PASO 4: Recargar estado
            await loadEventStatus();

            // Limpiar selección
            setSelectedLevels(prev => ({
                ...prev,
                [categoryId]: []
            }));

        } catch (err) {
            setError('Error al enviar postulación');
            console.error('Error en handleApplyToCategory:', err);
        } finally {
            setApplyingCategory(null);
        }
    };

    const handleApplyToAll = async () => {
        try {
            setApplyingAll(true);
            setError(null);
            setSuccess(null);

            // Filtrar categorías que NO tienen postulación
            const categoriesToApply = categories.filter(cat => !cat.judge_id);

            if (categoriesToApply.length === 0) {
                setError('Ya postulaste a todas las categorías disponibles');
                return;
            }

            const confirmMessage = `¿Estás seguro de postular a ${categoriesToApply.length} categoría(s) con TODOS sus niveles?\n\nEsto enviará tu postulación y deberás esperar aprobación del administrador.`;

            if (!window.confirm(confirmMessage)) {
                return;
            }

            if (!termsId) {
                setError('Error: No se pudo obtener el ID de términos');
                return;
            }

            let successCount = 0;
            let errorCount = 0;
            const errors: string[] = [];

            for (const category of categoriesToApply) {
                try {
                    // ✅ PASO 1: Postular a la categoría
                    const applyResponse = await judgeService.apply({
                        category_id: category.category_id,
                        terms_id: termsId
                    });

                    if (applyResponse.success && applyResponse.data?.judge_id) {
                        // ✅ PASO 2: Postular a TODOS los niveles
                        const levelsResponse = await judgeService.applyToLevels({
                            judge_id: applyResponse.data.judge_id,
                            levels: category.available_levels
                        });

                        if (levelsResponse.success) {
                            successCount++;
                        } else {
                            errorCount++;
                            errors.push(`${category.category_name}: ${levelsResponse.message}`);
                        }
                    } else {
                        errorCount++;
                        errors.push(`${category.category_name}: ${applyResponse.message}`);
                    }
                } catch (err) {
                    errorCount++;
                    errors.push(`${category.category_name}: Error desconocido`);
                }
            }

            // ✅ Recargar estado
            await loadEventStatus();

            // ✅ Mostrar resultado
            if (errorCount === 0) {
                setSuccess(`✅ Postulación enviada a ${successCount} categoría(s) con todos sus niveles. Espera aprobación del administrador.`);
            } else if (successCount > 0) {
                setError(`⚠️ Postulación parcial: ${successCount} OK, ${errorCount} con errores.\n${errors.join('\n')}`);
            } else {
                setError(`❌ Error al postular: ${errors.join('\n')}`);
            }

        } catch (err) {
            setError('Error al postular a todas las categorías');
            console.error('Error en handleApplyToAll:', err);
        } finally {
            setApplyingAll(false);
        }
    };

    // ✅ Verificar si un nivel ya tiene aplicación
    const getLevelApplicationStatus = (
        category: CategoryJudgeStatus,
        level: string
    ): 'not_applied' | 'pending' | 'approved' | 'rejected' => {
        if (!category.level_applications) return 'not_applied';

        const app = category.level_applications.find(la => la.level === level);
        if (!app) return 'not_applied';

        return app.approval_status as 'pending' | 'approved' | 'rejected';
    };

    if (isLoading) {
        return <Loading message="Cargando categorías..." />;
    }

    return (
        <div className="judge-category-selection">
            <Card variant="glass">
                <div className="selection-header">
                    <h2>✅ Términos Aceptados - Selecciona Categorías</h2>
                    <h3>🏆 {eventTitle}</h3>
                </div>

                {error && (
                    <ErrorMessage
                        message={error}
                        type="error"
                        onClose={() => setError(null)}
                    />
                )}

                {success && (
                    <div className="success-message">
                        <div className="success-content">
                            <span className="success-icon">✅</span>
                            <span className="success-text">{success}</span>
                            <button
                                className="success-close"
                                onClick={() => setSuccess(null)}
                                aria-label="Cerrar"
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                )}

                {/* Botón inscribirse en todas */}
                <div className="apply-all-section">
                    <Button
                        variant="primary"
                        size="lg"
                        fullWidth
                        onClick={handleApplyToAll}
                        loading={applyingAll}
                        disabled={applyingAll || categories.every(cat => cat.judge_id !== null)}
                    >
                        Inscribirme en TODA la Competencia
                        <span className="btn-subtitle">
                            (Todas las categorías, todos los niveles)
                        </span>
                    </Button>
                </div>

                <div className="categories-divider">
                    <span>Categorías de Competencia</span>
                </div>

                {/* Grid de categorías */}
                <div className="categories-grid">
                    {categories.map(category => (
                        <Card
                            key={category.category_id}
                            className="category-card"
                            variant="elevated"
                        >
                            {/* Imagen */}
                            <div className="category-image">
                                <img
                                    src={getCategoryImageUrl(category.category_id)}
                                    alt={category.category_name}
                                    onError={(e) => {
                                        e.currentTarget.onerror = null; // Prevenir loop infinito
                                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f3f4f6" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="48" fill="%239ca3af"%3E%F0%9F%93%B8%3C/text%3E%3C/svg%3E';
                                    }}
                                />
                            </div>

                            {/* Info */}
                            <div className="category-info">
                                <h4>{category.category_name}</h4>
                                <p className="category-type">PESTAÑAS</p>
                            </div>

                            {/* Botón seleccionar todos */}
                            {!category.judge_id && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    fullWidth
                                    onClick={() => handleSelectAllLevels(category.category_id)}
                                >
                                    Seleccionar Todos ▼
                                </Button>
                            )}

                            {/* Checkboxes de niveles */}
                            <div className="levels-selection">
                                {category.available_levels.map(level => {
                                    const status = getLevelApplicationStatus(category, level);
                                    const isSelected = selectedLevels[category.category_id]?.includes(level) || false;
                                    const isDisabled = status !== 'not_applied';

                                    return (
                                        <label
                                            key={level}
                                            className={`level-checkbox ${isDisabled ? 'disabled' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected || status === 'approved'}
                                                onChange={() => !isDisabled && handleToggleLevel(category.category_id, level)}
                                                disabled={isDisabled}
                                            />
                                            <span>{level}</span>
                                            {status !== 'not_applied' && (
                                                <span className={`level-status level-status--${status}`}>
                                                    {status === 'approved' && '✅'}
                                                    {status === 'pending' && '⏳'}
                                                    {status === 'rejected' && '❌'}
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>

                            {/* Estado de postulación de categoría */}
                            {category.approval_status && (
                                <div className={`category-status category-status--${category.approval_status}`}>
                                    {category.approval_status === 'approved' && '✅ Aprobado como Juez'}
                                    {category.approval_status === 'pending' && '⏳ Esperando Aprobación'}
                                    {category.approval_status === 'rejected' && '❌ Postulación Rechazada'}
                                </div>
                            )}

                            {/* Botón postular */}
                            {!category.judge_id && (
                                <Button
                                    variant="primary"
                                    fullWidth
                                    onClick={() => handleApplyToCategory(category.category_id)}
                                    disabled={
                                        !selectedLevels[category.category_id]?.length ||
                                        applyingCategory === category.category_id
                                    }
                                    loading={applyingCategory === category.category_id}
                                >
                                    Postular
                                </Button>
                            )}

                            {/* Info adicional si está aprobado */}
                            {category.approval_status === 'approved' && (
                                <div className="category-approved-info">
                                    <p>✅ Ya estás aprobado como juez en esta categoría</p>
                                    <p className="info-subtitle">
                                        Puedes postular a más niveles si lo deseas
                                    </p>
                                </div>
                            )}
                        </Card>
                    ))}
                </div>
            </Card>
        </div>
    );
};

export default JudgeCategorySelection;